import { Check, ChevronsUpDown, Store } from 'lucide-react';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Popover from '@radix-ui/react-popover';
import { useDashboard } from '@/hooks/useDashboardContext';
import { useOutlets } from '@/hooks/useOutlets';
import { cn } from '@/lib/utils';

interface OutletOption {
  id: string;
  name: string;
  city: string;
  type: 'qsr' | 'dine_in';
}

export function OutletSelector() {
  const { outletId, setOutletId } = useDashboard();
  const [open, setOpen] = useState(false);
  const { data: outlets = [] } = useOutlets();
  const navigate = useNavigate();
  const location = useLocation();

  const current =
    outletId === 'all' ? null : (outlets as OutletOption[]).find((o) => o.id === outletId) ?? null;

  /**
   * Routes that scope to a single outlet by URL: switching outlet in the
   * dropdown should navigate, not just update context.
   */
  const handleSelect = (id: string | 'all') => {
    setOutletId(id);
    setOpen(false);
    if (location.pathname.startsWith('/outlets/')) {
      navigate(id === 'all' ? '/outlets' : `/outlets/${id}`);
    } else if (location.pathname.startsWith('/sales/items/')) {
      // Item detail respects the outlet filter via context — no navigation.
    }
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        className={cn(
          'inline-flex h-9 items-center gap-2 rounded-lg border border-border/60 bg-card/80 px-3 text-sm font-medium shadow-soft',
          'transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        )}
      >
        <Store className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="truncate">{current ? current.name : 'All outlets'}</span>
        {current && (
          <span className="text-2xs text-muted-foreground">{current.city}</span>
        )}
        <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground" />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className="z-50 w-64 rounded-lg border border-border/60 bg-popover p-1 shadow-elevated data-[state=open]:animate-fade-in"
        >
          <button
            onClick={() => handleSelect('all')}
            className={cn(
              'flex w-full items-center justify-between rounded-md px-2.5 py-2 text-sm hover:bg-accent',
              outletId === 'all' && 'bg-accent',
            )}
          >
            <span className="font-medium">All outlets</span>
            {outletId === 'all' && <Check className="h-3.5 w-3.5" />}
          </button>
          <div className="my-1 h-px bg-border/60" />
          {outlets.map((o) => (
            <button
              key={o.id}
              onClick={() => handleSelect(o.id)}
              className={cn(
                'flex w-full items-center justify-between rounded-md px-2.5 py-2 text-sm hover:bg-accent',
                outletId === o.id && 'bg-accent',
              )}
            >
              <span className="flex items-center gap-2">
                <span className="font-medium">{o.name}</span>
                <span className="text-2xs text-muted-foreground">{o.city}</span>
                <span className="rounded bg-muted px-1.5 py-0.5 text-2xs text-muted-foreground">
                  {o.type === 'qsr' ? 'QSR' : 'Dine-in'}
                </span>
              </span>
              {outletId === o.id && <Check className="h-3.5 w-3.5" />}
            </button>
          ))}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
